import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LinkIcon from '@mui/icons-material/Link';
import DiMarkdown from './DiMarkdown';

type SearchPageProps = {
  initialQuery: string;
};

type SearchCompany = {
  ticker?: string;
  name?: string;
  industry?: string;
  value_score?: number;
  consideration?: string;
  decision?: string;
  payback_time?: number;
  exchange_symbol?: string;
  exchange_name?: string;
};

type SearchPage = {
  title?: string;
  url?: string;
  description?: string;
};

type SearchResult = {
  query?: string;
  intent?: string;
  answer?: string;
  companies?: SearchCompany[];
  pages_on_decode_investing?: SearchPage[];
};

// Value score color helper
const getScoreColor = (score: number): string => {
  if (score >= 80) return '#059669'; // green
  if (score >= 60) return '#0891b2'; // teal
  if (score >= 40) return '#d97706'; // amber
  return '#dc2626'; // red
};

// Decision badge helper
const getDecisionStyle = (decision: string): { bg: string; text: string } => {
  const d = decision.toLowerCase();
  if (d.includes('buy') || d.includes('strong')) return { bg: '#dcfce7', text: '#166534' };
  if (d.includes('hold') || d.includes('wait')) return { bg: '#fef3c7', text: '#92400e' };
  if (d.includes('sell') || d.includes('avoid')) return { bg: '#fee2e2', text: '#991b1b' };
  return { bg: '#f3f4f6', text: '#374151' };
};

const resultsCache: Record<string, SearchResult> = {};

export default function SearchResultsPage({ initialQuery }: SearchPageProps) {
  const [inputValue, setInputValue] = React.useState(initialQuery || '');
  const [value, setValue] = React.useState<string | null>(initialQuery || null);
  const [options, setOptions] = React.useState<string[]>([]);
  const [result, setResult] = React.useState<SearchResult | null>(
    (initialQuery && resultsCache[initialQuery]) || null
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const debounce = (func: Function, wait: number) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const searchCache = React.useRef<Record<string, string[]>>({});

  const fetchAutoComplete = React.useCallback(
    debounce(async (searchTerm: string) => {
      if (searchTerm.length < 2) {
        setOptions([]);
        return;
      }

      if (searchCache.current[searchTerm]) {
        setOptions(searchCache.current[searchTerm]);
        return;
      }

      try {
        const response = await fetch(`/search/autocomplete?q=${encodeURIComponent(searchTerm)}`);
        const data = await response.json();
        const completions: string[] = Array.isArray(data?.completions) ? data.completions : [];
        searchCache.current[searchTerm] = completions;
        setOptions(completions);
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
        setOptions([]);
      }
    }, 300),
    []
  );

  const fetchResults = React.useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;

    if (resultsCache[trimmed]) {
      setResult(resultsCache[trimmed]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/search/results?q=${encodeURIComponent(trimmed)}`, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const data = (await response.json()) as SearchResult;
      resultsCache[trimmed] = data;
      setResult(data);
    } catch (e) {
      console.error('Error fetching search results:', e);
      setError('Could not load search results. Please try again.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (initialQuery) {
      fetchResults(initialQuery);
    }
  }, [initialQuery, fetchResults]);

  const runSearch = async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;

    const url = `/search?q=${encodeURIComponent(trimmed)}`;
    window.history.replaceState(window.history.state, '', url);

    await fetchResults(trimmed);
  };

  const clearSearch = () => {
    setInputValue('');
    setValue(null);
    setOptions([]);
    setResult(null);
    window.history.replaceState(window.history.state, '', '/search');
  };

  const pages = result?.pages_on_decode_investing || [];
  const companies = result?.companies || [];
  const answer = typeof result?.answer === 'string' ? result.answer : '';

  const hasAnswer = answer.trim().length > 0;
  const hasPages = pages.length > 0;
  const hasCompanies = companies.length > 0;
  const hasResults = hasAnswer || hasPages || hasCompanies;

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(12px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          .di-search-page {
            max-width: 1140px;
            margin: 0 auto;
            padding: 32px 20px 48px;
            min-height: 60vh;
            width: 100%;
            box-sizing: border-box;
          }

          @media (max-width: 640px) {
            .di-search-page {
              padding: 20px 16px 32px;
            }
          }

          .di-search-header {
            margin-bottom: 32px;
          }

          .di-search-form {
            display: flex;
            gap: 12px;
            align-items: stretch;
            width: 100%;
            box-sizing: border-box;
          }

          @media (max-width: 640px) {
            .di-search-form {
              gap: 8px;
            }
          }

          .di-search-input-wrapper {
            flex: 1;
            position: relative;
            min-width: 0;
            box-sizing: border-box;
          }

          .di-search-input-wrapper .MuiOutlinedInput-root {
            background: #fff;
            border-radius: 16px;
            font-size: 16px;
            box-shadow: 0 2px 12px rgba(0,0,0,0.08);
            transition: box-shadow 0.2s ease, border-color 0.2s ease;
          }

          @media (max-width: 640px) {
            .di-search-input-wrapper .MuiOutlinedInput-root {
              border-radius: 12px;
              font-size: 14px;
            }
          }

          .di-search-input-wrapper .MuiOutlinedInput-root:hover {
            box-shadow: 0 4px 20px rgba(0,0,0,0.12);
          }

          .di-search-input-wrapper .MuiOutlinedInput-root.Mui-focused {
            box-shadow: 0 4px 24px rgba(59, 130, 246, 0.18);
          }

          .di-search-input-wrapper .MuiOutlinedInput-notchedOutline {
            border-color: #e5e7eb;
            border-width: 1px;
          }

          .di-search-input-wrapper .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
            border-color: #d1d5db;
          }

          .di-search-input-wrapper .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: #3b82f6;
            border-width: 2px;
          }

          .di-search-input-wrapper .MuiInputLabel-root {
            color: #9ca3af;
          }

          .di-search-btn {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: #fff;
            border-radius: 14px;
            padding: 14px 18px;
            min-width: 52px;
            box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
            transition: all 0.2s ease;
          }

          @media (max-width: 640px) {
            .di-search-btn {
              border-radius: 12px;
              padding: 12px 14px;
              min-width: 48px;
            }
          }

          .di-search-btn:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
            transform: translateY(-1px);
          }

          .di-search-btn:active {
            transform: translateY(0);
          }

          .di-results-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 24px;
            animation: fadeInUp 0.4s ease-out;
            width: 100%;
            box-sizing: border-box;
          }

          @media (max-width: 640px) {
            .di-results-grid {
              gap: 16px;
            }
          }

          @media (min-width: 960px) {
            .di-results-grid {
              grid-template-columns: 1fr 340px;
              align-items: start;
            }
          }

          .di-main-content {
            display: flex;
            flex-direction: column;
            gap: 24px;
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
          }

          @media (max-width: 640px) {
            .di-main-content {
              gap: 16px;
            }
          }

          .di-sidebar {
            display: flex;
            flex-direction: column;
            gap: 20px;
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
          }

          .di-card {
            background: #fff;
            border-radius: 16px;
            padding: 24px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04);
            transition: box-shadow 0.2s ease;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            overflow-wrap: break-word;
            word-wrap: break-word;
          }

          @media (max-width: 640px) {
            .di-card {
              padding: 16px;
              border-radius: 12px;
            }
          }

          .di-card:hover {
            box-shadow: 0 2px 8px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.06);
          }

          .di-answer-card {
            line-height: 1.7;
            color: #1f2937;
            overflow-wrap: break-word;
            word-wrap: break-word;
          }

          @media (max-width: 640px) {
            .di-answer-card {
              line-height: 1.6;
            }
          }

          .di-answer-card h1, .di-answer-card h2, .di-answer-card h3 {
            color: #111827;
            margin-top: 1.5em;
            margin-bottom: 0.5em;
          }

          .di-answer-card h1:first-child, .di-answer-card h2:first-child, .di-answer-card h3:first-child {
            margin-top: 0;
          }

          .di-answer-card p {
            margin-bottom: 1em;
          }

          .di-answer-card a {
            color: #2563eb;
            text-decoration: none;
            font-weight: 500;
          }

          .di-answer-card a:hover {
            text-decoration: underline;
          }

          .di-section-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #6b7280;
            margin-bottom: 16px;
            padding-bottom: 12px;
            border-bottom: 1px solid #f3f4f6;
            width: 100%;
            box-sizing: border-box;
          }

          .di-section-label svg {
            font-size: 16px;
            opacity: 0.7;
          }

          .di-pages-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
            width: 100%;
            box-sizing: border-box;
          }

          .di-page-item {
            padding: 12px 14px;
            margin: -12px -14px;
            border-radius: 10px;
            transition: background-color 0.15s ease;
          }

          @media (max-width: 640px) {
            .di-page-item {
              padding: 10px 12px;
              margin: -10px -12px;
            }
          }

          .di-page-item:hover {
            background-color: #f9fafb;
          }

          .di-page-item a {
            font-weight: 600;
            font-size: 15px;
            color: #1f2937;
            text-decoration: none;
            display: block;
            margin-bottom: 4px;
            word-break: break-word;
            overflow-wrap: break-word;
          }

          @media (max-width: 640px) {
            .di-page-item a {
              font-size: 14px;
            }
          }

          .di-page-item a:hover {
            color: #2563eb;
          }

          .di-page-item-title {
            font-weight: 600;
            color: #1f2937;
          }

          .di-page-desc {
            font-size: 13px;
            color: #6b7280;
            line-height: 1.5;
            word-break: break-word;
            overflow-wrap: break-word;
          }

          @media (max-width: 640px) {
            .di-page-desc {
              font-size: 12px;
            }
          }

          .di-companies-section {
            margin-top: 8px;
          }

          .di-companies-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
            width: 100%;
            box-sizing: border-box;
          }

          @media (max-width: 640px) {
            .di-companies-grid {
              gap: 12px;
            }
          }

          @media (min-width: 640px) {
            .di-companies-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }

          @media (min-width: 1100px) {
            .di-companies-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }

          .di-company-card {
            background: #fff;
            border-radius: 14px;
            padding: 20px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04);
            transition: all 0.2s ease;
            border: 1px solid #f3f4f6;
            cursor: pointer;
            text-decoration: none;
            display: block;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            overflow-wrap: break-word;
            word-wrap: break-word;
            min-width: 0;
          }

          @media (max-width: 640px) {
            .di-company-card {
              padding: 16px;
              border-radius: 12px;
            }
          }

          .di-company-card:hover {
            box-shadow: 0 4px 16px rgba(0,0,0,0.08), 0 8px 32px rgba(0,0,0,0.06);
            transform: translateY(-2px);
            border-color: #e5e7eb;
          }

          .di-company-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 12px;
            gap: 12px;
            width: 100%;
            min-width: 0;
          }

          @media (max-width: 640px) {
            .di-company-header {
              margin-bottom: 10px;
              gap: 8px;
            }
          }

          .di-company-header > div:first-child {
            flex: 1;
            min-width: 0;
          }

          .di-company-ticker {
            font-size: 18px;
            font-weight: 700;
            color: #111827;
            text-decoration: none;
            word-break: break-word;
            overflow-wrap: break-word;
          }

          @media (max-width: 640px) {
            .di-company-ticker {
              font-size: 16px;
            }
          }

          .di-company-ticker:hover {
            color: #2563eb;
          }

          .di-company-name {
            font-size: 13px;
            color: #6b7280;
            margin-top: 2px;
            word-break: break-word;
            overflow-wrap: break-word;
          }

          @media (max-width: 640px) {
            .di-company-name {
              font-size: 12px;
            }
          }

          .di-score-badge {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 700;
            color: #fff;
            flex-shrink: 0;
          }

          @media (max-width: 640px) {
            .di-score-badge {
              width: 36px;
              height: 36px;
              font-size: 12px;
              border-radius: 8px;
            }
          }

          .di-company-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 12px;
            width: 100%;
          }

          @media (max-width: 640px) {
            .di-company-meta {
              gap: 6px;
              margin-top: 10px;
            }
          }

          .di-meta-tag {
            display: inline-flex;
            align-items: center;
            padding: 4px 10px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 500;
            word-break: break-word;
            overflow-wrap: break-word;
            max-width: 100%;
          }

          @media (max-width: 640px) {
            .di-meta-tag {
              font-size: 11px;
              padding: 3px 8px;
            }
          }

          .di-industry-tag {
            background: #f3f4f6;
            color: #4b5563;
          }

          .di-exchange-tag {
            background: #eff6ff;
            color: #1d4ed8;
          }

          .di-payback-tag {
            background: #fef3c7;
            color: #92400e;
          }

          .di-decision-badge {
            margin-top: 12px;
            padding: 6px 12px;
            border-radius: 8px;
            font-size: 12px;
            font-weight: 600;
            display: inline-block;
            word-break: break-word;
            overflow-wrap: break-word;
            max-width: 100%;
          }

          .di-loading {
            display: flex;
            flex-direction: column;
            gap: 20px;
            animation: fadeInUp 0.3s ease-out;
          }

          .di-skeleton {
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
            border-radius: 8px;
          }

          .di-skeleton-lg {
            height: 200px;
            border-radius: 16px;
          }

          .di-skeleton-md {
            height: 80px;
            border-radius: 12px;
          }

          .di-skeleton-sm {
            height: 24px;
            width: 60%;
          }

          .di-error {
            background: #fef2f2;
            border: 1px solid #fecaca;
            color: #991b1b;
            padding: 16px 20px;
            border-radius: 12px;
            font-size: 14px;
          }

          .di-empty {
            text-align: center;
            padding: 64px 24px;
            color: #9ca3af;
          }

          .di-empty-icon {
            font-size: 48px;
            margin-bottom: 16px;
            opacity: 0.5;
          }

          .di-empty-text {
            font-size: 16px;
            color: #6b7280;
          }

          .di-clear-btn {
            color: #9ca3af;
            transition: color 0.15s ease;
          }

          .di-clear-btn:hover {
            color: #4b5563;
          }

          .dark-mode .di-search-page {
            color: #ffffff;
          }

          .dark-mode .di-search-input-wrapper .MuiOutlinedInput-root {
            background: #222328;
            color: #ffffff;
            box-shadow: 0 2px 12px rgba(0,0,0,0.24);
          }

          .dark-mode .di-search-input-wrapper .MuiOutlinedInput-root:hover {
            box-shadow: 0 4px 20px rgba(0,0,0,0.32);
          }

          .dark-mode .di-search-input-wrapper .MuiOutlinedInput-root.Mui-focused {
            box-shadow: 0 4px 24px rgba(182, 109, 255, 0.2);
          }

          .dark-mode .di-search-input-wrapper .MuiOutlinedInput-notchedOutline {
            border-color: #322f2f;
          }

          .dark-mode .di-search-input-wrapper .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
            border-color: #9c9fa6;
          }

          .dark-mode .di-search-input-wrapper .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: #b66dff;
          }

          .dark-mode .di-search-input-wrapper .MuiInputLabel-root,
          .dark-mode .di-clear-btn {
            color: #9c9fa6;
          }

          .dark-mode .di-search-btn {
            background: linear-gradient(135deg, #b66dff 0%, #8f4de8 100%);
            color: #ffffff;
            box-shadow: 0 2px 8px rgba(182, 109, 255, 0.3);
          }

          .dark-mode .di-search-btn:hover {
            background: linear-gradient(135deg, #9a55ff 0%, #7b39db 100%);
            box-shadow: 0 4px 16px rgba(182, 109, 255, 0.35);
          }

          .dark-mode .di-card,
          .dark-mode .di-company-card {
            background: #222328;
            color: #ffffff;
            box-shadow: 0 1px 3px rgba(0,0,0,0.24), 0 4px 12px rgba(0,0,0,0.2);
            border: 1px solid #322f2f;
          }

          .dark-mode .di-card:hover,
          .dark-mode .di-company-card:hover {
            box-shadow: 0 2px 8px rgba(0,0,0,0.28), 0 8px 24px rgba(0,0,0,0.24);
          }

          .dark-mode .di-answer-card,
          .dark-mode .di-answer-card h1,
          .dark-mode .di-answer-card h2,
          .dark-mode .di-answer-card h3,
          .dark-mode .di-page-item a,
          .dark-mode .di-company-ticker {
            color: #ffffff;
          }

          .dark-mode .di-section-label,
          .dark-mode .di-page-desc,
          .dark-mode .di-company-name,
          .dark-mode .di-empty,
          .dark-mode .di-empty-text {
            color: #9c9fa6;
          }

          .dark-mode .di-section-label {
            border-bottom-color: #322f2f;
          }

          .dark-mode .di-page-item:hover {
            background-color: #1a191d;
          }

          .dark-mode .di-page-item a:hover,
          .dark-mode .di-answer-card a,
          .dark-mode .di-company-ticker:hover {
            color: #b66dff;
          }

          .dark-mode .di-page-item-title {
            color: #ffffff;
          }

          .dark-mode .di-industry-tag {
            background: #151519;
            color: #d8d8d8;
          }

          .dark-mode .di-exchange-tag {
            background: rgba(25, 138, 227, 0.18);
            color: #90caf9;
          }

          .dark-mode .di-payback-tag {
            background: rgba(246, 215, 19, 0.16);
            color: #f6e384;
          }

          .dark-mode .di-skeleton {
            background: linear-gradient(90deg, #1a191d 25%, #322f2f 50%, #1a191d 75%);
            background-size: 200% 100%;
          }

          .dark-mode .di-error {
            background: rgba(254, 124, 150, 0.14);
            border-color: rgba(254, 124, 150, 0.35);
            color: #ffbf96;
          }
        `}
      </style>

      <div className="di-search-page">
        <div className="di-search-header">
          <form
            className="di-search-form"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch(inputValue);
            }}
          >
            <div className="di-search-input-wrapper">
              <Autocomplete
                value={value}
                onChange={(event: React.SyntheticEvent, newValue: string | null, reason) => {
                  setValue(newValue);
                  if (typeof newValue === 'string') {
                    setInputValue(newValue);
                    if (reason === 'selectOption') {
                      runSearch(newValue);
                    }
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                  fetchAutoComplete(newInputValue);
                }}
                selectOnFocus
                handleHomeEndKeys
                id="search-autocomplete"
                options={options}
                getOptionLabel={(option) => option}
                fullWidth
                freeSolo
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="q"
                    placeholder="Search for stocks, companies, or ask a question..."
                    variant="outlined"
                    autoComplete="off"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {inputValue && (
                            <IconButton
                              size="small"
                              onClick={clearSearch}
                              aria-label="clear search"
                              className="di-clear-btn"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
            <IconButton type="submit" aria-label="search" className="di-search-btn">
              <SearchIcon />
            </IconButton>
          </form>
        </div>

        {loading && (
          <div className="di-loading">
            <div className="di-skeleton di-skeleton-lg" />
            <div className="di-skeleton di-skeleton-md" />
            <div style={{ display: 'flex', gap: '16px' }}>
              <div className="di-skeleton di-skeleton-md" style={{ flex: 1 }} />
              <div className="di-skeleton di-skeleton-md" style={{ flex: 1 }} />
            </div>
          </div>
        )}

        {error && <div className="di-error">{error}</div>}

        {!loading && !error && hasResults && (
          <div className="di-results-grid">
            <div className="di-main-content">
              {hasAnswer && (
                <div className="di-card di-answer-card">
                  <DiMarkdown showTableDownloadButton={false}>{answer}</DiMarkdown>
                </div>
              )}

              {hasCompanies && (
                <div className="di-companies-section">
                  <div className="di-section-label">
                    <TrendingUpIcon />
                    <span>Related Companies</span>
                  </div>
                  <div className="di-companies-grid">
                    {companies.map((c, idx) => (
                      <a
                        href={c.ticker ? `/ticker/${c.ticker}` : '#'}
                        className="di-company-card"
                        key={`${c.ticker || 'company'}-${idx}`}
                      >
                        <div className="di-company-header">
                          <div>
                            <div className="di-company-ticker">{c.ticker || 'N/A'}</div>
                            {c.name && <div className="di-company-name">{c.name}</div>}
                          </div>
                          {typeof c.value_score === 'number' && c.value_score !== 0 && (
                            <div
                              className="di-score-badge"
                              style={{ backgroundColor: getScoreColor(c.value_score) }}
                            >
                              {c.value_score}
                            </div>
                          )}
                        </div>

                        <div className="di-company-meta">
                          {c.industry && (
                            <span className="di-meta-tag di-industry-tag">{c.industry}</span>
                          )}
                          {c.exchange_symbol && (
                            <span className="di-meta-tag di-exchange-tag">{c.exchange_symbol}</span>
                          )}
                          {typeof c.payback_time === 'number' && c.payback_time !== 0 && (
                            <span className="di-meta-tag di-payback-tag">
                              {c.payback_time} yr payback
                            </span>
                          )}
                        </div>

                        {c.decision && (
                          <div
                            className="di-decision-badge"
                            style={{
                              backgroundColor: getDecisionStyle(c.decision).bg,
                              color: getDecisionStyle(c.decision).text,
                            }}
                          >
                            {c.decision}
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {hasPages && (
              <div className="di-sidebar">
                <div className="di-card">
                  <div className="di-section-label">
                    <LinkIcon />
                    <span>Related Pages</span>
                  </div>
                  <div className="di-pages-list">
                    {pages.map((p, idx) => (
                      <div className="di-page-item" key={`${p.url || 'page'}-${idx}`}>
                        {p.url ? (
                          <a href={p.url}>{p.title || p.url}</a>
                        ) : (
                          <div className="di-page-item-title">{p.title}</div>
                        )}
                        {p.description && <div className="di-page-desc">{p.description}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && !error && !hasResults && inputValue && (
          <div className="di-empty">
            <div className="di-empty-icon">🔍</div>
            <div className="di-empty-text">No results found. Try a different search.</div>
          </div>
        )}
      </div>
    </>
  );
}
